import HBIcon from "@/components/HBIcon";
import { API_CACHEKEY } from "@/constants/cacheKey";
import useAppStore from "@/context/store";
import { getBoard, getDomain, getUser } from "@/services";
import useRequest from "@/utils/useReq";
import { useMemoizedFn, useSetState } from "ahooks";
import { useRouter } from "next/router";
import { useEffect, useMemo, useRef } from "react";
import QuickSearch from "./QuickSearch";
import styles from "./index.module.less";

const SEARCH_TYPE = {
  pin: "pin",
  board: "board",
  user: "user",
  material: "material",
  ai: "aigc",
} as const;

// 当前页面，提示接下来的搜索范围用
export const PAGE_SCOPE = {
  ALL: "all", // 在全局搜索页面
  USER: "user", // 用户个人页
  BOARD: "board", // 在具体画板的采集搜索结果页
  DOMAIN: "domain",
  FOLLOWING: "following", // 个人关注页
  // ... 专题
} as const;

type TSearchType = typeof SEARCH_TYPE;

type IPageScope =
  | {
      type: "all" | "user" | "following";
      data: IUser | null;
    }
  | {
      type: "board";
      data: IBoardInfo | null;
    }
  | {
      type: "domain";
      data: IDomainInfo | null;
    };

type TSearchState = {
  searchType: TSearchType[keyof TSearchType];
  pageScope: IPageScope;
};

type ScopeConfItem = {
  /** 搜索范围唯一标识 */
  id: string;
  /** 根据当前路由信息判断是否匹配当前搜索范围 */
  cond: (pathname: string, query: any) => boolean;
  /** 用于获取当前搜索范围的状态 */
  getSearchState: () => Promise<TSearchState | null>;
  /** 用于获取当前搜索范围的文案 */
  getScopeTxt?: (data: IUser | IDomainInfo | IBoardInfo) => string | undefined;
  /** 用于获取当前搜索范围的参数 */
  getSearchParam: (searchState: TSearchState) => any;
};

type wrapQuickSearchProps = (
  prop: Omit<Parameters<typeof QuickSearch>[0], "pageScope">
) => JSX.Element;

/**
 * 封装的搜索范围的状态管理
 */
export default function useSearchScope() {
  const router = useRouter();
  const { urlname, boardId, board_id, domain_id, domainId } = router.query;
  const user = useAppStore((s) => s.user);
  /** 当前搜索范围id */
  const currentScopeId = useRef("");
  const [searchState, setSearchState] = useSetState<TSearchState>({
    searchType: null,
    pageScope: {
      type: PAGE_SCOPE.ALL,
      data: user,
    },
  });

  const quickSearchRef = useRef<wrapQuickSearchProps>(null);
  const { runAsync: getBoardCache } = useRequest(getBoard, {
    cacheKey: `${API_CACHEKEY.BOARD}_${board_id || boardId}`,
    staleTime: 3000,
    manual: true,
  });
  const { runAsync: getUserCache } = useRequest(getUser, {
    cacheKey: `${API_CACHEKEY.USER}_${urlname}`,
    staleTime: 3000,
    manual: true,
  });
  const { runAsync: getDomainCache } = useRequest(getDomain, {
    cacheKey: `${API_CACHEKEY.DOMAIN}_${domain_id || domainId}`,
    staleTime: 3000,
    manual: true,
  });
  /**搜索范围的配置 */
  const scopeConfList = useMemo<ScopeConfItem[]>(() => {
    const { urlname, boardId, board_id, domain_id, domainId } = router.query;

    // 获取用户搜索范围的文案
    const getUserScopeTxt = (targetUser: IUser) =>
      user?.urlname === targetUser?.urlname ? "我的" : targetUser.username;
    // 获取用户搜索范围的数据
    const getUserScopeData = (searchType, scopeType) => async () => {
      let { user: data, err } =
        user?.urlname === urlname || !urlname
          ? { user, err: false }
          : await getUserCache({ urlname: urlname as string });

      if (err) return null;
      return {
        searchType: searchType,
        pageScope: {
          type: scopeType,
          data,
        },
      };
    };

    // 获取画板搜索范围的文案
    const getBoardScopeTxt = (board: IBoardInfo) => board?.title;
    // 获取画板搜索范围的数据
    const getBoardScopeData = (searchType, scopeType) => async () => {
      const { err, board } = await getBoardCache({
        board_id: (board_id || boardId) as string,
      });
      if (err) return null;
      return {
        searchType: searchType,
        pageScope: {
          type: scopeType,
          data: board,
        },
      };
    };

    // 获取画板搜索范围的文案
    const getDomainScopeTxt = (domain: IDomainInfo) => domain?.domain_name;
    // 获取画板搜索范围的数据
    const getDomainState = (searchType, scopeType) => async () => {
      const domain = await getDomainCache({
        // 来源页面参数是 domainId，搜索页面参数是 domain_id
        domain_id: (domain_id || domainId) as string,
      });
      if (domain?.err) return null;
      return {
        searchType: searchType,
        pageScope: {
          type: scopeType,
          data: domain,
        },
      };
    };

    return [
      {
        id: "searchFollingBoard",
        cond: (pathname, { tabId, urlname, type, subtype }) =>
          (pathname.includes("/user/[urlname]/following/[tabId]") &&
            tabId === "board") ||
          (pathname === "/search" &&
            urlname &&
            type === "following" &&
            subtype == "board"),
        getSearchState: getUserScopeData(
          SEARCH_TYPE.board,
          PAGE_SCOPE.FOLLOWING
        ),
        getScopeTxt: getUserScopeTxt,
        getSearchParam: (searchState) => ({
          type: PAGE_SCOPE.FOLLOWING,
          subtype: SEARCH_TYPE.board,
          urlname: (searchState.pageScope.data as IUser)?.urlname,
        }),
      },
      {
        id: "searchFollingUser",
        cond: (pathname, { urlname, type, subtype }) =>
          pathname === "/user/[urlname]/following" ||
          (pathname === "/search" &&
            urlname &&
            type === "following" &&
            subtype == "user"),
        getSearchState: getUserScopeData(
          SEARCH_TYPE.user,
          PAGE_SCOPE.FOLLOWING
        ),
        getScopeTxt: getUserScopeTxt,
        getSearchParam: (searchState) => ({
          type: "following",
          subtype: SEARCH_TYPE.user,
          urlname: (searchState.pageScope.data as IUser)?.urlname,
        }),
      },
      {
        id: "searchBoardPin",
        cond: (pathname, { board_id }) =>
          pathname === "/boards/[boardId]" ||
          (pathname === "/search" && board_id && !isNaN(+board_id)),
        getSearchState: getBoardScopeData(SEARCH_TYPE.pin, PAGE_SCOPE.BOARD),
        getScopeTxt: getBoardScopeTxt,
        getSearchParam: (searchState) => ({
          board_id: (searchState.pageScope.data as IBoardInfo)?.board_id,
        }),
      },
      {
        id: "searchDomainPin",
        cond: (pathname, { domain_id }) =>
          pathname === "/domains/[domainId]" ||
          (pathname === "/search" && domain_id),
        getSearchState: getDomainState(SEARCH_TYPE.pin, PAGE_SCOPE.DOMAIN),
        getScopeTxt: getDomainScopeTxt,
        getSearchParam: (searchState) => ({
          domain_id: (searchState.pageScope.data as IDomainInfo)?.domain_id,
        }),
      },
      {
        id: "searchUserPin",
        cond: (pathname, { urlname, openTab, subtype }) =>
          (pathname === "/user/[urlname]/[openTab]" && openTab === "pins") ||
          (pathname === "/search" && urlname && subtype === SEARCH_TYPE.pin),
        getSearchState: getUserScopeData(SEARCH_TYPE.pin, PAGE_SCOPE.USER),
        getScopeTxt: getUserScopeTxt,
        getSearchParam: (searchState) => ({
          urlname: (searchState.pageScope.data as IUser)?.urlname,
          subtype: SEARCH_TYPE.pin,
        }),
      },
      {
        id: "searchUserBoard",
        cond: (pathname, { urlname, subtype }) =>
          (pathname.includes("/user/[urlname]") &&
            pathname !== "/user/[urlname]/following/[tabId]") ||
          (pathname === "/search" && urlname && subtype === SEARCH_TYPE.board),
        getSearchState: getUserScopeData(SEARCH_TYPE.board, PAGE_SCOPE.USER),
        getScopeTxt: getUserScopeTxt,
        getSearchParam: (searchState) => ({
          urlname: (searchState.pageScope.data as IUser)?.urlname,
          subtype: SEARCH_TYPE.board,
        }),
      },
      {
        id: "searchAI",
        cond: (pathname, { type }) =>
          pathname === "/ai" ||
          (pathname === "/search" && type === SEARCH_TYPE.ai),
        getSearchState: getUserScopeData(SEARCH_TYPE.ai, PAGE_SCOPE.USER),
        getSearchParam: () => ({
          type: SEARCH_TYPE.ai,
        }),
      },
      {
        id: "searchMaterial",
        cond: (pathname, { type }) =>
          pathname === "/materials" ||
          pathname === "/calendars/[[...festival]]" ||
          (pathname === "/search" && type === SEARCH_TYPE.material),
        getSearchState: getUserScopeData(SEARCH_TYPE.material, PAGE_SCOPE.USER),
        getSearchParam: () => ({
          type: SEARCH_TYPE.material,
        }),
      },
    ];
  }, [router, user]);

  // 切换页面时，根据当前页面的路由，确定搜索范围数据，与快速搜索组件
  useEffect(() => {
    const { pathname, query } = router;
    const match = scopeConfList.find((s) => s.cond(pathname, query));
    if (match) {
      currentScopeId.current = match.id;
      match
        .getSearchState()
        .then((state) => {
          setSearchState(state);
          // 封装快速搜索组件
          quickSearchRef.current = (props) =>
            QuickSearch({ ...props, pageScope: state.pageScope });
        })
        .catch();
    } else {
      setSearchState({
        searchType: null,
        pageScope: {
          type: PAGE_SCOPE.ALL,
          data: user,
        },
      });
      // 封装快速搜索组件
      quickSearchRef.current = (props) =>
        QuickSearch({
          ...props,
          pageScope: {
            type: PAGE_SCOPE.ALL,
            data: user,
          },
        });
      currentScopeId.current = "";
    }
  }, [user, router, scopeConfList, setSearchState]);

  // 切换搜索范围
  const changeScope = (targeId: string | null) => {
    const match = scopeConfList.find((s) => s.id === targeId);
    if (match) {
      match.getSearchState().then((state) => setSearchState(state));
      currentScopeId.current = match.id;
    } else {
      setSearchState({
        searchType: null,
        pageScope: {
          type: PAGE_SCOPE.ALL,
          data: user,
        },
      });
      currentScopeId.current = "";
    }
  };
  /**
   * 获取当前搜索范围的搜索需要的参数
   * @param query 额外的查询参数
   * @returns {Object} 搜索参数
   */
  const getSearchParam = useMemoizedFn((query = {}) => {
    const { pathname, query: oldQuery } = router;
    // 根据路由，获取当前页面的默认搜索范围
    const defaultConf = scopeConfList.find((s) => s.cond(pathname, oldQuery));
    // 判断是否切换了搜索范围：当前页面的默认搜索范围与当前搜索范围不一致
    const hasChangeScope = currentScopeId.current !== (defaultConf?.id || "");

    const match = scopeConfList.find((s) => s.id === currentScopeId.current);
    // 获取当前搜索范围的查询参数
    const pageScopeQuery = match ? match.getSearchParam(searchState) : {};

    // 需要保留旧的查询参数情况
    // 当前处于搜索页面|AI广场页，且没有切换搜索范围（点击搜索我的）
    const shouldKeepOldQuery =
      (pathname === "/search" || pathname === "/ai") && !hasChangeScope;

    return shouldKeepOldQuery
      ? { ...oldQuery, ...query, ...pageScopeQuery }
      : { ...query, ...pageScopeQuery };
  });

  // 当前搜索范围组件： 商用素材、我的画板|采集、用户的画板|采集、全局，画板内采集，来源内采集，AI作品，
  const currentSearchScope = useMemo(() => {
    const { searchType, pageScope } = searchState;
    if (currentScopeId.current === "") {
      return null;
    }

    const searchTypeName = (
      {
        [SEARCH_TYPE.board]: "画板",
        [SEARCH_TYPE.pin]: "采集",
        [SEARCH_TYPE.material]: "商用素材",
        [SEARCH_TYPE.ai]: "AI作品",
        [SEARCH_TYPE.user]: "用户",
      } as const
    )[searchType];

    const scopeIcon =
      {
        [SEARCH_TYPE.ai]: "aigc",
        [SEARCH_TYPE.material]: "ic_copyright",
      }[searchType] ||
      (
        {
          [PAGE_SCOPE.BOARD]: "dashboard",
          [PAGE_SCOPE.FOLLOWING]: "link",
          [PAGE_SCOPE.DOMAIN]: "link",
          [PAGE_SCOPE.USER]: "ic_account",
        } as const
      )[pageScope.type];

    let pageScopeTxt = "";
    const match = scopeConfList.find((s) => s.id === currentScopeId.current);
    if (match) {
      pageScopeTxt = match.getScopeTxt
        ? match.getScopeTxt(pageScope.data) || ""
        : "";
    }

    if (pageScope.type === PAGE_SCOPE.FOLLOWING) {
      pageScopeTxt =
        pageScopeTxt.slice(0, 7) +
        (pageScopeTxt.length > 7 ? "...关注的" : "关注的");
    } else {
      pageScopeTxt =
        pageScopeTxt.slice(0, 10) + (pageScopeTxt.length > 10 ? "..." : "");
    }

    if (!searchType) {
      return null;
    }

    return {
      label: (
        <div className={styles.tag}>
          <HBIcon className={styles.icon} type={scopeIcon} />
          {pageScopeTxt ? (
            <>
              <span className={styles.text}>{pageScopeTxt}</span>
              <span className={styles.type}>{searchTypeName}</span>
            </>
          ) : (
            <span className={styles.text}>{searchTypeName}</span>
          )}
        </div>
      ),
    };
  }, [scopeConfList, searchState]);

  return {
    /**当前搜索code */
    currentScopeIdRef: currentScopeId,
    /**搜索内容类型 */
    searchType: searchState.searchType,
    /**搜索范围 */
    pageScope: searchState.pageScope,
    /**切换搜索范围 */
    changeScope,
    /**获取当前搜索范围的搜索需要的参数 */
    getSearchParam,
    /**当前搜索范围组件 */
    currentSearchScope,
    /**快速搜索入口封装组件 */
    QuickSearch: quickSearchRef.current,
  };
}
